declare var $;

export type SummerNoteConfig = {
    options: any,
    toolbar: string[][],
    dropFileUrl: string,
    imageUploadAllowed: boolean,
    height: number
}

const XSRF_TOKEN_NAME = '__RequestVerificationToken';

export default class TalkAboutSummernoteFactory {

    private editors = new Map<string, any>();
    private summerNoteConfig: SummerNoteConfig = null;
    private xsrfToken: string;  

    public GetConfig(): SummerNoteConfig {
        return this.summerNoteConfig;
    }

    public async InitConfig(el: HTMLDivElement): Promise<SummerNoteConfig> {
        const self = this;

        if (self.summerNoteConfig === null) {
            try {
                await self.initializeXsrfToken();

                self.summerNoteConfig = {
                    options: null,
                    toolbar: null,
                    dropFileUrl: null,
                    imageUploadAllowed: false,
                    height: 200
                };
                const summernoteConfigPath = el.dataset.summernoteConfigUrl;
                const summernoteToolbarPath = el.dataset.summernoteToolbarConfigUrl;
                const summernoteLanguage = el.dataset.summernoteConfigLanguage;
                self.summerNoteConfig.dropFileUrl = el.dataset.summernoteConfigDropfileuploadurl;
                
                // Extract height from data attribute with fallback to 200
                const heightAttr = el.dataset.summernoteConfigHeight;
                self.summerNoteConfig.height = heightAttr ? parseInt(heightAttr, 10) : 200;
                                
                await fetch(summernoteConfigPath, {
                    headers: { 'Cache-Control': 'no-cache, no-store' }
                })
                    .then((response) => response.json())
                    .then((json) => {
                        self.summerNoteConfig.options = json;
                        self.summerNoteConfig.options.lang = summernoteLanguage || "en-US";                          
                    });

                await fetch("/api/talk/CanUploadImages", {
                    headers: { 'Cache-Control': 'no-cache, no-store' }
                })
                    .then((response) => response.json())
                    .then((json) => {
                        self.summerNoteConfig.imageUploadAllowed = (json == true);                        
                    });             

                await fetch(summernoteToolbarPath, {
                    headers: { 'Cache-Control': 'no-cache, no-store' }
                })
                    .then((response) => response.json())
                    .then((json:string[][]) => {
                        self.summerNoteConfig.toolbar = json;                    
                    });


                //if image upload is not allowed then remove image button from toolbar               
                if (!self.summerNoteConfig.imageUploadAllowed) {
                    self.summerNoteConfig.toolbar = self.summerNoteConfig.toolbar.map(([key, values]) => {
                        if (key === "insert" && Array.isArray(values)) {
                            return [key, values.filter(value => value !== "picture")];
                        }
                        return [key, values];
                    }) as string[][];
                }
            }
            catch (error) {
                console.error("Error fetching Summernote configuration:", error);
                throw error;
            }
        }
        return this.summerNoteConfig;
    }

    public getEditor(elementId: string): any {
        const self = this;
        let editor = self.editors.get(elementId);
        

        if (!editor) {
            editor = $('#' + elementId).summernote({
                ...self.summerNoteConfig.options,
                toolbar: self.summerNoteConfig.toolbar,
                height: self.summerNoteConfig.height,
                minHeight: null,
                maxHeight: null,
                callbacks: {
                    onImageUpload: async function (files) {
                        //you have to add this callback in order to handle this, which is happening in
                        //editor.on('summernote.image.upload', function (we, files) {
                        //await self.imageUpload(files);
                    },
                    onMediaDelete: function ($target, editor, $editable) {                                               
                        const imageUrl = $target[0].src;
                        //console.log(`delete:${imageUrl}`)                       
                      
                        var formData = new FormData();
                        formData.append(XSRF_TOKEN_NAME, self.xsrfToken);
                        formData.append("imageUrl", imageUrl);

                        try {
                            //fire and forget
                            fetch("/talkimage/deleteimage", {
                                method: 'POST',
                                body: formData
                            });                            
                        }
                        catch (error) {
                            console.error(error.message);
                        }   
                    },
                }
            });

            //ref used for callback
            const thisEditor = editor;
                       
            if (self.summerNoteConfig.imageUploadAllowed) {
                editor.on('summernote.image.upload', async function (we, files) {
                    // upload image to server and create imgNode...                
                    await self.imageUpload(files, thisEditor);
                });
            }

            self.editors.set(elementId, editor);
        }

        return editor;
    }


    private initializeXsrfToken = async (): Promise<void> => {
        let self = this;

        try {
            const response = await fetch("/talk/token");
            if (!response.ok) {
                throw new Error(`Response status: ${response.status}`);
            }
          
            let json = await response.json();
            self.xsrfToken = json.token;
           
            console.log(json);

        } catch (error) {
            console.error(error.message);
        }
    }

    private imageUpload = async (files, editor): Promise<void> => {
        if (files && files.length > 0) {
            for (var i = 0; i < files.length; i++) {

                var name = files[i].name; //usually this will always be image.png
                var baseName = name.substring(0, name.lastIndexOf('.')) || name;
                var ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase();
                var date = new Date().toISOString().replace(/:|\./g, '-').substring(0, 21);
                var newName = baseName + '_' + date + '.' + ext;
                var file = this.renameFile(files[i], newName);

                await this.uploadImageFile(file, editor);
            }
        }
    }

    private renameFile = (originalFile, newName) => {
        return new File([originalFile], newName, {
            type: originalFile.type,
            lastModified: originalFile.lastModified,
        });
    }

    private uploadImageFile = async (file, editor): Promise<void> => {
        const self = this;

        switch (file.type) {
            case "image/jpeg":
            case "image/jpg":
            case "image/gif":
            case "image/png":
            case "image/svg+xml":
                
                var formData = new FormData();                
                formData.append(XSRF_TOKEN_NAME, this.xsrfToken);
                formData.append(file.name, file);

                try {                    
                    let response = await fetch(this.summerNoteConfig.dropFileUrl, {
                        method: 'POST',
                        body: formData                        
                    });
                    if (!response.ok) {
                        throw new Error(`Response status: ${response.status}`);
                    }

                    let json = await response.json();

                    if (json[0].errorMessage) {                        
                        console.error(json[0].errorMessage);
                        return;
                    }
                   
                    editor.summernote('insertImage', json[0].resizedUrl || json[0].originalUrl, json[0].name);                   
                }
                catch (error) {
                    console.error(error.message);
                }               

                break;
        }
    }
}
